/*###########################################################################
 // Add Card picker (Alpine component)
 //#########################################################################*/

import { showChecklist } from './_confirmDialog'

function readPickerConfig() {
  const el = document.getElementById('add-card-picker-config')
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

/** Soft-refresh My Chases so new cards appear without a hard reload. */
function refreshCardsPage() {
  const url = `${window.location.pathname}${window.location.search}`
  const swup = window.swup

  if (swup?.navigate) {
    swup.cache?.delete?.(url)
    swup.hooks?.once?.('visit:start', (visit) => {
      if (visit?.scroll) visit.scroll.reset = false
    })
    swup.navigate(url, {
      animate: false,
      history: 'replace',
      cache: { read: false, write: true },
    })
    return
  }

  // Fallback when SPA nav isn’t available: swap #swup HTML in place.
  fetch(url, {
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.text()
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const next = doc.querySelector('#swup')
      const current = document.querySelector('#swup')
      if (!next || !current) {
        window.location.reload()
        return
      }
      const Alpine = window.Alpine
      try {
        Alpine?.destroyTree?.(current)
      } catch {
        // ignore
      }
      const fresh = next.cloneNode(true)
      current.replaceWith(fresh)
      try {
        Alpine?.initTree?.(fresh)
      } catch {
        // ignore
      }
      window.dispatchEvent(
        new CustomEvent('app:navigated', {
          detail: { url: new URL(window.location.href) },
        }),
      )
    })
    .catch(() => {
      window.location.reload()
    })
}

/**
 * @param {ReturnType<typeof readPickerConfig>} config
 */
export function createAddCardPicker(config) {
  return {
    pickerOpen: false,
    // choose | sets | cards | pokedex | pokedex-cards
    step: 'choose',
    sets: [],
    setsLoading: false,
    setsLoaded: false,
    setsError: '',
    selectedSet: null,
    cards: [],
    cardsLoading: false,
    cardsError: '',
    search: '',
    addingId: null,
    addedCount: 0,
    setFilter: '',

    pokedexEntries: [],
    pokedexLoading: false,
    pokedexLoadingMore: false,
    pokedexError: '',
    pokedexSearch: '',
    pokedexPage: 0,
    pokedexHasMore: false,
    pokedexTotal: 0,
    selectedPokemon: null,
    _pokedexSearchTimer: null,
    _onRequest: null,

    lists: Array.isArray(config?.lists) ? config.lists.map((l) => ({ ...l })) : [],
    selectedListIds: [],
    /** When true (By list view, no list pre-selected), prompt if they add with none chosen. */
    listAssignmentRequired: false,

    init() {
      this.pickerOpen = false
      this._onRequest = (event) => {
        const detail = event?.detail
        if (detail && typeof detail === 'object') {
          this.openPicker({
            setName: detail.setName == null || detail.setName === '' ? null : String(detail.setName),
            listId: detail.listId != null && detail.listId !== '' ? Number(detail.listId) : null,
          })
          return
        }
        const setName = detail == null || detail === '' ? null : String(detail)
        this.openPicker({ setName })
      }
      window.addEventListener('add-card-request', this._onRequest)
    },

    destroy() {
      if (this._onRequest) {
        window.removeEventListener('add-card-request', this._onRequest)
      }
      if (this._pokedexSearchTimer) {
        clearTimeout(this._pokedexSearchTimer)
        this._pokedexSearchTimer = null
      }
      if (this.pickerOpen) {
        this.pickerOpen = false
        window.app?.enableScroll?.()
      }
    },

    get filteredSets() {
      const q = this.setFilter.trim().toLowerCase()
      if (!q) return this.sets
      return this.sets.filter((set) => {
        const hay = `${set.name} ${set.code || ''}`.toLowerCase()
        return hay.includes(q)
      })
    },

    get filteredCards() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.cards
      return this.cards.filter((card) => {
        const hay = `${card.name} ${card.numberLabel} ${card.category} ${card.setName || ''}`.toLowerCase()
        return hay.includes(q)
      })
    },

    get ariaLabel() {
      if (this.step === 'choose') return 'Add a card'
      if (this.step === 'sets') return 'Choose a set'
      if (this.step === 'cards') return `Add cards from ${this.selectedSet?.name || 'set'}`
      if (this.step === 'pokedex') return 'Choose a Pokémon'
      if (this.step === 'pokedex-cards') return `Add cards for ${this.selectedPokemon?.name || 'Pokémon'}`
      return 'Add a card'
    },

    isListGroupMode() {
      const el = document.getElementById('cards-summary')
      return el?.dataset?.groupBy === 'list'
    },

    /**
     * @param {string|{setName?: string|null, listId?: number|null}|null} [options]
     */
    async openPicker(options = null) {
      let setName = null
      let listId = null
      if (typeof options === 'string') {
        setName = options
      } else if (options && typeof options === 'object') {
        setName = options.setName == null || options.setName === '' ? null : String(options.setName)
        listId =
          options.listId != null && options.listId !== '' && Number(options.listId) > 0
            ? Number(options.listId)
            : null
      }

      this.pickerOpen = true
      this.search = ''
      this.setFilter = ''
      this.pokedexSearch = ''
      this.cardsError = ''
      this.setsError = ''
      this.pokedexError = ''
      this.selectedListIds =
        listId && this.lists.some((l) => l.id === listId) ? [listId] : []
      this.listAssignmentRequired = this.isListGroupMode() && !listId
      window.app?.disableScroll?.()

      // Deep-link from a set accordion skips the chooser.
      if (setName) {
        if (!this.setsLoaded) {
          await this.loadSets()
        }
        const match = this.sets.find(
          (s) => s.name === setName || s.rawSetName === setName,
        )
        if (match && (match.cardCount ?? 0) > 0) {
          await this.selectSet(match)
          return
        }
        this.step = 'sets'
        this.selectedSet = null
        this.cards = []
        return
      }

      this.step = 'choose'
      this.selectedSet = null
      this.selectedPokemon = null
      this.cards = []
      this.pokedexEntries = []
    },

    closePicker({ refreshIfAdded = true } = {}) {
      const shouldRefresh = refreshIfAdded && this.addedCount > 0
      this.pickerOpen = false
      this.addedCount = 0
      window.app?.enableScroll?.()
      if (shouldRefresh) {
        refreshCardsPage()
      }
    },

    async startBySet() {
      this.selectedPokemon = null
      this.pokedexEntries = []
      if (!this.setsLoaded) {
        await this.loadSets()
      }
      this.step = 'sets'
      this.selectedSet = null
      this.cards = []
      this.setFilter = ''
    },

    async startByPokedex() {
      this.selectedSet = null
      this.selectedPokemon = null
      this.cards = []
      this.step = 'pokedex'
      this.pokedexSearch = ''
      await this.loadPokedex({ reset: true })
    },

    backToChoose() {
      this.step = 'choose'
      this.selectedSet = null
      this.selectedPokemon = null
      this.cards = []
      this.search = ''
      this.setFilter = ''
      this.pokedexSearch = ''
      this.cardsError = ''
      this.pokedexError = ''
    },

    backToSets() {
      this.step = 'sets'
      this.selectedSet = null
      this.cards = []
      this.search = ''
      this.cardsError = ''
    },

    backToPokedex() {
      this.step = 'pokedex'
      this.selectedPokemon = null
      this.cards = []
      this.search = ''
      this.cardsError = ''
      this.afterPaint(() => this.fillPokedexIfNeeded())
    },

    onPokedexSearchInput() {
      if (this._pokedexSearchTimer) {
        clearTimeout(this._pokedexSearchTimer)
      }
      this._pokedexSearchTimer = setTimeout(() => {
        this.loadPokedex({ reset: true })
      }, 280)
    },

    afterPaint(fn) {
      return new Promise((resolve) => {
        const done = () => {
          try {
            fn?.()
          } finally {
            resolve()
          }
        }
        if (typeof this.$nextTick === 'function') {
          this.$nextTick(done)
        } else {
          requestAnimationFrame(() => requestAnimationFrame(done))
        }
      })
    },

    pickerScrollEl() {
      return this.$refs?.pickerBody || this.$el?.querySelector?.('.desk-picker__body') || null
    },

    onPickerBodyScroll() {
      if (this.step !== 'pokedex') return
      if (!this.pokedexHasMore || this.pokedexLoading || this.pokedexLoadingMore) return

      const el = this.pickerScrollEl()
      if (!el) return

      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
      if (remaining < 320) {
        this.loadPokedex()
      }
    },

    /** Keep fetching while the list doesn’t fill the panel. */
    fillPokedexIfNeeded() {
      if (this.step !== 'pokedex') return
      if (!this.pokedexHasMore || this.pokedexLoading || this.pokedexLoadingMore) return

      const el = this.pickerScrollEl()
      if (!el) return

      if (el.scrollHeight <= el.clientHeight + 40) {
        this.loadPokedex()
      }
    },

    async loadSets() {
      if (!config?.setsUrl) {
        this.setsError = 'Picker config missing.'
        return
      }

      this.setsLoading = true
      this.setsError = ''
      try {
        const res = await fetch(config.setsUrl, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const data = await res.json()
        this.sets = Array.isArray(data.sets) ? data.sets : []
        this.setsLoaded = true
      } catch {
        this.setsError = 'Couldn’t load sets. Please try again.'
      } finally {
        this.setsLoading = false
      }
    },

    async loadPokedex({ reset = false } = {}) {
      if (!config?.pokedexUrl) {
        this.pokedexError = 'Picker config missing.'
        return
      }

      if (reset) {
        this.pokedexEntries = []
        this.pokedexPage = 0
        this.pokedexHasMore = true
        this.pokedexTotal = 0
        const el = this.pickerScrollEl()
        if (el) el.scrollTop = 0
      }

      if (!this.pokedexHasMore && !reset) return
      if (this.pokedexLoading || this.pokedexLoadingMore) return

      const nextPage = this.pokedexPage + 1
      const isFirst = nextPage === 1
      if (isFirst) {
        this.pokedexLoading = true
      } else {
        this.pokedexLoadingMore = true
      }
      this.pokedexError = ''

      try {
        const url = new URL(config.pokedexUrl, window.location.origin)
        url.searchParams.set('page', String(nextPage))
        const q = this.pokedexSearch.trim()
        if (q) url.searchParams.set('q', q)

        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        if (!data.success) throw new Error('Bad response')

        const entries = Array.isArray(data.entries) ? data.entries : []
        this.pokedexEntries = reset || isFirst
          ? entries
          : this.pokedexEntries.concat(entries)
        this.pokedexPage = data.page ?? nextPage
        this.pokedexHasMore = Boolean(data.hasMore)
        this.pokedexTotal = Number(data.total) || 0
      } catch {
        this.pokedexError = 'Couldn’t load the Pokédex. Please try again.'
        this.pokedexHasMore = false
      } finally {
        this.pokedexLoading = false
        this.pokedexLoadingMore = false
        if (this.step === 'pokedex' && this.pokedexHasMore) {
          await this.afterPaint(() => this.fillPokedexIfNeeded())
        }
      }
    },

    async selectSet(set) {
      if (!set || (set.cardCount ?? 0) < 1) return

      this.selectedSet = set
      this.selectedPokemon = null
      this.step = 'cards'
      this.search = ''
      this.cards = []
      this.cardsLoading = true
      this.cardsError = ''

      try {
        const url = new URL(config.cardsUrl, window.location.origin)
        url.searchParams.set('setName', set.rawSetName ?? set.name)
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const data = await res.json()
        this.cards = Array.isArray(data.cards) ? data.cards : []
      } catch {
        this.cardsError = 'Couldn’t load cards for this set.'
      } finally {
        this.cardsLoading = false
      }
    },

    async selectPokemon(pokemon) {
      if (!pokemon || (pokemon.cardCount ?? 0) < 1) return

      this.selectedPokemon = pokemon
      this.selectedSet = null
      this.step = 'pokedex-cards'
      this.search = ''
      this.cards = []
      this.cardsLoading = true
      this.cardsError = ''

      try {
        const url = new URL(config.pokemonCardsUrl, window.location.origin)
        url.searchParams.set('pokemonId', String(pokemon.id))
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const data = await res.json()
        this.cards = Array.isArray(data.cards) ? data.cards : []
        if (data.pokemon) {
          this.selectedPokemon = { ...pokemon, ...data.pokemon }
        }
      } catch {
        this.cardsError = 'Couldn’t load cards for this Pokémon.'
      } finally {
        this.cardsLoading = false
      }
    },

    toggleList(listId) {
      const id = Number(listId)
      if (!id) return
      if (this.selectedListIds.includes(id)) {
        this.selectedListIds = this.selectedListIds.filter((x) => x !== id)
      } else {
        this.selectedListIds = [...this.selectedListIds, id]
      }
    },

    async resolveListIdsForAdd() {
      if (this.selectedListIds.length > 0 || !this.listAssignmentRequired || !this.lists.length) {
        return this.selectedListIds
      }

      const picked = await showChecklist({
        eyebrow: 'Lists',
        title: 'Add to a list?',
        message: `Choose where “${this.pendingCardName || 'this card'}” should go. Leave none selected to keep it unlisted.`,
        label: 'Lists',
        items: this.lists.map((list) => ({
          value: String(list.id),
          label: list.name,
          checked: false,
        })),
        confirmLabel: 'Add card',
        cancelLabel: 'Cancel',
      })

      if (picked === null) {
        return null
      }

      const ids = picked.map((v) => Number(v)).filter((id) => id > 0)
      this.selectedListIds = ids
      // Remember their choice for the rest of this picker session.
      this.listAssignmentRequired = false
      return ids
    },

    async addCard(card) {
      if (this.addingId || !config) return
      this.addingId = card.id
      this.pendingCardName = card.name

      try {
        const listIds = await this.resolveListIdsForAdd()
        if (listIds === null) {
          return
        }

        const body = new FormData()
        body.append(config.csrfName, config.csrfToken)
        body.append('cardId', String(card.id))
        listIds.forEach((id) => body.append('listIds[]', String(id)))

        const res = await fetch(config.addUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
        const data = await res.json()

        if (!data.success) {
          window.dispatchEvent(
            new CustomEvent('site-toast', { detail: { message: data.error || 'Couldn’t add card.' } }),
          )
          return
        }

        this.cards = this.cards.filter((c) => c.id !== card.id)
        if (data.added) {
          this.addedCount += 1
          window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'chasedFirstCard' } }))
        }

        // Keep Pokédex tile counts roughly in sync while browsing.
        if (this.selectedPokemon && this.step === 'pokedex-cards') {
          const nextCount = Math.max(0, (this.selectedPokemon.cardCount ?? 1) - 1)
          this.selectedPokemon = { ...this.selectedPokemon, cardCount: nextCount }
          this.pokedexEntries = this.pokedexEntries.map((entry) =>
            entry.id === this.selectedPokemon.id
              ? { ...entry, cardCount: nextCount }
              : entry,
          )
        }

        window.dispatchEvent(
          new CustomEvent('site-toast', {
            detail: { message: data.message || `Added ${card.name}.` },
          }),
        )
      } catch {
        window.dispatchEvent(
          new CustomEvent('site-toast', { detail: { message: 'Couldn’t add card. Please try again.' } }),
        )
      } finally {
        this.addingId = null
        this.pendingCardName = ''
      }
    },
  }
}

export function registerAddCardPicker(Alpine) {
  Alpine.data('addCardPicker', () => {
    const config = readPickerConfig()
    return createAddCardPicker(config || {})
  })
}

/** Plain JS open triggers — event delegation, works across SPA navigations */
export function bindAddCardTriggers() {
  if (window.__addCardDelegateBound) return
  window.__addCardDelegateBound = true

  document.addEventListener('click', (event) => {
    const listBtn = event.target.closest('[data-open-add-card-list]')
    if (listBtn) {
      event.preventDefault()
      event.stopPropagation()
      const listId = Number(listBtn.getAttribute('data-open-add-card-list'))
      window.dispatchEvent(
        new CustomEvent('add-card-request', {
          detail: { listId: listId > 0 ? listId : null },
        }),
      )
      return
    }

    const btn = event.target.closest('[data-open-add-card]')
    if (!btn) return

    event.preventDefault()
    event.stopPropagation()

    const setName = btn.getAttribute('data-open-add-card')
    window.dispatchEvent(
      new CustomEvent('add-card-request', {
        detail:
          setName === '' || setName == null
            ? { setName: null, listId: null }
            : { setName, listId: null },
      }),
    )
  })
}
